import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router'
import { GlobalCreateButton } from '../components/common/GlobalCreateButton'
import UserDropdown from '../components/header/UserDropdown'
import Button from '../components/ui/button/Button'
import { useBusiness } from '../context/BusinessContext'
import { useSidebar } from '../context/SidebarContext'
import Filter from '../pages/Business/Filters/Filter'
import { FilterType, useCommon } from '../context/CommonContext'
import { useProfessional } from '../context/ProfessionalContext'
import { useProduct } from '../context/ProductContext'
import { useTarget } from '../context/TargetContext'
import { CircleX, Search } from 'lucide-react'

const AppHeader: React.FC = () => {
    const [isApplicationMenuOpen, setApplicationMenuOpen] = useState(false)
    const { isMobileOpen, toggleSidebar, toggleMobileSidebar } = useSidebar()
    const { filters, handleSearch, filterByTime } = useBusiness()
    const { filtersOptions } = useCommon()
    const { professionals } = useProfessional()
    const { products } = useProduct()
    const { targets } = useTarget()
    const location = useLocation()
    const module = location.pathname

    // convert to array with "id" and "label"
    const formattedProfessionals = professionals.map(professional => ({
        id: Number(professional.id),
        label: String(professional.name)
    })) as FilterType[]

    const formattedProducts = products.map(product => ({
        id: Number(product.id),
        label: String(product.name)
    })) as FilterType[]

    const formattedTargets = targets.map(target => ({
        id: Number(target.id),
        label: String(target.name)
    })) as FilterType[]

    const handleToggle = () => {
        if (window.innerWidth >= 991) {
            toggleSidebar()
        } else {
            toggleMobileSidebar()
        }
    }

    const toggleApplicationMenu = () => {
        setApplicationMenuOpen(!isApplicationMenuOpen)
    }
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
                event.preventDefault()
                inputRef.current?.focus()
            }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [])

    return (
        <>
            {/* <pre className='text-white mt-4 ml-6 text-base'>{JSON.stringify(filters)}</pre> */}
            <header className='sticky top-0 flex w-full bg-white border-gray-200 z-30 dark:border-gray-800 dark:bg-gray-900 lg:border-b'>
                <div className='flex flex-col items-center justify-between flex-grow lg:flex-row lg:px-6'>
                    {/* Left */}
                    <div className='flex items-center justify-between w-full gap-2 px-3 py-3 border-b border-gray-200 dark:border-gray-800 sm:gap-4 lg:justify-normal lg:border-b-0 lg:px-0 lg:py-4'>
                        <button
                            className='items-center justify-center w-10 h-10 text-gray-500 border-gray-200 rounded-lg z-99999 dark:border-gray-800 lg:flex dark:text-gray-400 lg:h-11 lg:w-11 lg:border'
                            onClick={handleToggle}
                            aria-label='Toggle Sidebar'
                        >
                            {isMobileOpen ? (
                                <svg
                                    width='24'
                                    height='24'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                >
                                    <path
                                        fillRule='evenodd'
                                        clipRule='evenodd'
                                        d='M6.21967 7.28131C5.92678 6.98841 5.92678 6.51354 6.21967 6.22065C6.51256 5.92775 6.98744 5.92775 7.28033 6.22065L11.999 10.9393L16.7176 6.22078C17.0105 5.92789 17.4854 5.92788 17.7782 6.22078C18.0711 6.51367 18.0711 6.98855 17.7782 7.28144L13.0597 12L17.7782 16.7186C18.0711 17.0115 18.0711 17.4863 17.7782 17.7792C17.4854 18.0721 17.0105 18.0721 16.7176 17.7792L11.999 13.0607L7.28033 17.7794C6.98744 18.0722 6.51256 18.0722 6.21967 17.7794C5.92678 17.4865 5.92678 17.0116 6.21967 16.7187L10.9384 12L6.21967 7.28131Z'
                                        fill='currentColor'
                                    />
                                </svg>
                            ) : (
                                <svg
                                    width='16'
                                    height='12'
                                    viewBox='0 0 16 12'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                >
                                    <path
                                        fillRule='evenodd'
                                        clipRule='evenodd'
                                        d='M0.583252 1C0.583252 0.585788 0.919038 0.25 1.33325 0.25H14.6666C15.0808 0.25 15.4166 0.585786 15.4166 1C15.4166 1.41421 15.0808 1.75 14.6666 1.75L1.33325 1.75C0.919038 1.75 0.583252 1.41422 0.583252 1ZM0.583252 11C0.583252 10.5858 0.919038 10.25 1.33325 10.25L14.6666 10.25C15.0808 10.25 15.4166 10.5858 15.4166 11C15.4166 11.4142 15.0808 11.75 14.6666 11.75L1.33325 11.75C0.919038 11.75 0.583252 11.4142 0.583252 11ZM1.33325 5.25C0.919038 5.25 0.583252 5.58579 0.583252 6C0.583252 6.41421 0.919038 6.75 1.33325 6.75L7.99992 6.75C8.41413 6.75 8.74992 6.41421 8.74992 6C8.74992 5.58579 8.41413 5.25 7.99992 5.25L1.33325 5.25Z'
                                        fill='currentColor'
                                    />
                                </svg>
                            )}
                            {/* Cross Icon */}
                        </button>

                        <Link to='/' className='lg:hidden'>
                            <img className='dark:hidden' src='./images/logo/logo.svg' alt='Logo' />
                            <img className='hidden dark:block' src='./images/logo/logo-dark.svg' alt='Logo' />
                        </Link>

                        <button
                            onClick={toggleApplicationMenu}
                            className='flex items-center justify-center w-10 h-10 text-gray-700 rounded-lg z-99999 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 lg:hidden'
                        >
                            <svg
                                width='24'
                                height='24'
                                viewBox='0 0 24 24'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <path
                                    fillRule='evenodd'
                                    clipRule='evenodd'
                                    d='M5.99902 10.4951C6.82745 10.4951 7.49902 11.1667 7.49902 11.9951V12.0051C7.49902 12.8335 6.82745 13.5051 5.99902 13.5051C5.1706 13.5051 4.49902 12.8335 4.49902 12.0051V11.9951C4.49902 11.1667 5.1706 10.4951 5.99902 10.4951ZM17.999 10.4951C18.8275 10.4951 19.499 11.1667 19.499 11.9951V12.0051C19.499 12.8335 18.8275 13.5051 17.999 13.5051C17.1706 13.5051 16.499 12.8335 16.499 12.0051V11.9951C16.499 11.1667 17.1706 10.4951 17.999 10.4951ZM13.499 11.9951C13.499 11.1667 12.8275 10.4951 11.999 10.4951C11.1706 10.4951 10.499 11.1667 10.499 11.9951V12.0051C10.499 12.8335 11.1706 13.5051 11.999 13.5051C12.8275 13.5051 13.499 12.8335 13.499 12.0051V11.9951Z'
                                    fill='currentColor'
                                />
                            </svg>
                        </button>

                        {module.includes('/negocio') && (
                            <>
                                <div className='hidden lg:block'>
                                    <form>
                                        <div className='relative'>
                                            <span className='absolute -translate-y-1/2 pointer-events-none left-4 top-1/2'>
                                                <svg
                                                    className='fill-gray-500 dark:fill-gray-400'
                                                    width='20'
                                                    height='20'
                                                    viewBox='0 0 20 20'
                                                    fill='none'
                                                    xmlns='http://www.w3.org/2000/svg'
                                                >
                                                    <path
                                                        fillRule='evenodd'
                                                        clipRule='evenodd'
                                                        d='M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z'
                                                        fill=''
                                                    />
                                                </svg>
                                            </span>

                                            {filters.search && (
                                                <CircleX
                                                    onClick={() => handleSearch('')}
                                                    className='w-5 h-5 ml-0 absolute right-3 top-3 cursor-pointer text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                                />
                                            )}

                                            <input
                                                ref={inputRef}
                                                type='text'
                                                placeholder='Pesquisar...'
                                                className='dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-10 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-800 dark:bg-gray-900 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 xl:w-[250px]'
                                                value={filters.search}
                                                onChange={e => handleSearch(e.target.value)}
                                            />
                                        </div>
                                    </form>
                                </div>

                                {/* Filter buttons */}
                                <div className='flex items-center gap-1 w-full'>
                                    <Button
                                        size='sm'
                                        variant={filters.time === 'today' ? 'primary' : 'outline'}
                                        onClick={() => filterByTime('today')}
                                    >
                                        <p>Hoje</p>
                                    </Button>
                                    <Button
                                        size='sm'
                                        variant={filters.time === 'thisWeek' ? 'primary' : 'outline'}
                                        onClick={() => filterByTime('thisWeek')}
                                    >
                                        <p>Esta semana</p>
                                    </Button>
                                    <Button
                                        size='sm'
                                        variant={filters.time === 'nextWeek' ? 'primary' : 'outline'}
                                        onClick={() => filterByTime('nextWeek')}
                                    >
                                        <p>Próxima semana</p>
                                    </Button>
                                    <Button
                                        size='sm'
                                        variant={filters.time === 'expired' ? 'primary' : 'outline'}
                                        onClick={() => filterByTime('expired')}
                                    >
                                        <p>Vencidos</p>
                                    </Button>
                                    <div className='w-full max-w-48'>
                                        <Filter
                                            title='Profissional'
                                            type='creators'
                                            selecteds={filters.creators}
                                            options={formattedProfessionals}
                                            disabledLabel
                                        />
                                    </div>
                                    <div className='w-full max-w-48'>
                                        <Filter
                                            title='Produto'
                                            type='products'
                                            selecteds={filters.products}
                                            options={formattedProducts}
                                            disabledLabel
                                        />
                                    </div>
                                    <div className='w-full max-w-48'>
                                        <Filter
                                            title='Etiqueta'
                                            type='targets'
                                            selecteds={filters.targets}
                                            options={formattedTargets}
                                            disabledLabel
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Right */}
                    <div
                        className={`${
                            isApplicationMenuOpen ? 'flex' : 'hidden'
                        } items-center justify-between gap-4 px-5 py-4 lg:flex shadow-theme-md lg:justify-end lg:px-0 lg:shadow-none`}
                    >
                        <div className='flex items-center gap-2 2xsm:gap-3'>
                            {/* <!-- Global Create Button --> */}
                            <GlobalCreateButton />
                            {/* <NotificationDropdown /> */}
                            {/* <!-- Notification Menu Area --> */}
                        </div>
                        {/* <!-- User Area --> */}
                        <UserDropdown />
                    </div>
                </div>
            </header>
        </>
    )
}

export default AppHeader
